import {
    getApp,
    getHostClientType,
    getHostedScenario,
    getHostHub,
    isRunningInMetaOSHub,
    TEAMS_HUB,
} from 'owa-config';
import { isHostAppFeatureEnabled } from 'owa-hostapp-feature-flags';
import { isBootFeatureEnabled } from 'owa-metatags/lib/isBootFeatureEnabled';
import { getOrigin } from 'owa-url/lib/getOrigin';
import { isMonitoringProbe } from './isMonitoringProbe';
import {
    MSAL_ENTERPRISE_PUBLIC_ORIGINS,
    PLACES_MSAL_ENTERPRISE_PUBLIC_ORIGINS,
} from './utils/constants';

/**
 * Checks if the request is enabled for business accounts
 **/
export function isMsalEnabledForBusiness(): boolean {
    if (isMonitoringProbe() && isBusinessDomain()) {
        // MSEXCHMON monitoring probe should return true to ensure anchor mailbox is set
        return true;
    }

    return (
        areMsalBusinessFlightsEnabledForCurrentApp() &&
        isHostAppFeatureEnabled('msalTokenFetch') &&
        isBusinessDomain()
    );
}

/**
 * Checks if this is a business domain
 **/
export function isBusinessDomain(): boolean {
    // get the origin of the request with trailing slash to compare against the OWA_RESOURCE_URL_ENTERPRISE urls.
    const origin = getOrigin();

    // If getOrigin is one of OWA public urls, then return true.
    if (MSAL_ENTERPRISE_PUBLIC_ORIGINS.some(re => re.test(origin))) {
        return true;
    }

    // check if the origin is a consumer domain
    return origin.toLowerCase().includes('live-int.com') || !origin.toLowerCase().includes('live');
}

function isRunningInTeamsIOS(): boolean {
    const hostHub = getHostHub();
    const clientType = (getHostClientType() || '').toLowerCase();

    return hostHub === TEAMS_HUB && (clientType === 'ios' || clientType === 'ipados');
}

function areMsalBusinessFlightsEnabledForCurrentApp(): boolean {
    const app = getApp();

    switch (app) {
        case 'DelegateAccess':
            return true;
        case 'AppHost':
        case 'BackgroundSync':
        case 'FilesHub':
        case 'Groups':
        case 'Mail':
        case 'MailDeepLink':
        case 'MyOutlook':
        case 'Oobe':
        case 'People':
        case 'PeopleNew':
        case 'Tasks':
            return isRunningInMetaOSHub() ? isBootFeatureEnabled('auth-msaljs-hosted') : true;
        case 'Calendar':
        case 'CalendarDeepLink':
            return isRunningInMetaOSHub()
                ? isBootFeatureEnabled('auth-msaljs-hostedcalendar-i2')
                : true;
        case 'CalendarDeepLinkOpx':
        case 'MailDeepLinkOpx':
        case 'Opx':
            return areMsalBusinessFlightsEnabledForCurrentOpx();
        case 'Addison':
        case 'Eventify':
        case 'QuickCalendar':
        case 'FindTime':
        case 'Meet':
            return true;
        case 'MicrosoftPlaces':
        case 'Places':
            // Only Teams iOS hosted scenario is still gated by a flight
            if (isRunningInMetaOSHub() && isRunningInTeamsIOS()) {
                return (
                    isBootFeatureEnabled('auth-msaljs-hostedplaces-teamsios') ||
                    isPlacesCloudDomain()
                );
            }
            return true;

        case 'Bookings':
        case 'BookingsC2':
        case 'BookingsMobile':
        case 'BookWithMe':
            return isRunningInMetaOSHub()
                ? isBootFeatureEnabled('auth-msaljs-hostedbookings')
                : true;
        case 'CopilotHub':
        case 'MiniMail':
        case 'PublishedCalendar':
        case undefined:
            // if you're reading this, you probably added a new app and wondered why the build broke.
            // If your app is meant to be an independent experience and is not a core OWA module/scenario, you may need your own
            // separate clientId. Otherwise, add your app to the first case. You
            // should not add yourself here to the `false` case. Contact owaauth with any questions.
            return false;
        default:
            return app satisfies never;
    }
}

function areMsalBusinessFlightsEnabledForCurrentOpx(): boolean {
    const opxView = getHostedScenario();

    switch (opxView) {
        case 'MyDay':
            return isBootFeatureEnabled('auth-msaljs-opx-myday');
        case 'RoomPanel':
            return isBootFeatureEnabled('auth-msaljs-opx-roompanel');
        case 'ActivityFeed':
            return isBootFeatureEnabled('auth-msaljs-opx-activityfeed');
        case 'FindTime':
        case 'FindTimePane':
            return isBootFeatureEnabled('auth-msaljs-schedulingpoll');
        default:
            return false;
    }
}

// This is a temporary solution until Places rolls out to App ID and MSAL to WW.
// Tracking at: ADO 292898
function isPlacesCloudDomain() {
    const origin = getOrigin();
    return PLACES_MSAL_ENTERPRISE_PUBLIC_ORIGINS.some(re => re.test(origin));
}
